import { useRef, useEffect } from 'react';
import clsx from 'clsx';
import Chartjs, { ChartOptions as ChartJsOptions, ChartDataset } from 'chart.js/auto';

// 프로퍼티 타입 정의
interface ChartProps {
  chartLabel: string; // 차트의 제목
  chartType: 'line' | 'bar' | 'doughnut' | 'pie'; // 차트의 종류
  chartData: ChartData; // 차트의 데이터
  chartOptions?: ChartJsOptions; // 차트의 옵션
  chartInfo?: {
    title: string | number; // 제목
    titleUnit?: string; // 제목 단위
    desc: string | number; // 설명
    descUnit?: string; // 설명 단위
  };
  className?: string; // 클래스명
}

// ChartData 타입 정의 (Chart.js에서 사용하는 타입에 맞게)
interface ChartData {
  datasets: Array<ChartDataset<'line' | 'bar' | 'doughnut' | 'pie', number[]>>;
}

// Chart 컴포넌트
export const Chart: React.FC<ChartProps> = ({
  chartLabel,
  chartType,
  chartData,
  chartOptions = {}, // 기본값 비어있는 객체로 초기화
  chartInfo,
  className,
  ...props
}) => {
  const chartRef = useRef<HTMLCanvasElement | null>(null);
  const chart = useRef<Chartjs | null>(null);

  const chartColor = ['#69A5FF', '#FF8C8C', '#FFC06E', '#88F03E', '#9E86FC'];

  useEffect(() => {
    // 기본 설정
    Chartjs.defaults.devicePixelRatio = 1;
    Chartjs.defaults.responsive = true;
    Chartjs.defaults.maintainAspectRatio = false;
    Chartjs.defaults.color = '#000';
    Chartjs.defaults.font.family = 'Pretendard, sans-serif';
    Chartjs.defaults.font.size = 14;
    Chartjs.defaults.font.weight = 400;
    Chartjs.defaults.borderColor = '#ABABAB';
    Chartjs.defaults.plugins.legend.position = 'bottom';
    Chartjs.defaults.plugins.legend.labels.padding = 30;
    Chartjs.defaults.plugins.legend.labels.boxWidth = 15;
    Chartjs.defaults.plugins.legend.labels.boxHeight = 15;
    Chartjs.defaults.elements.point.radius = 2;
    Chartjs.defaults.elements.line.borderWidth = 2;
    Chartjs.defaults.elements.line.borderDash = [3, 3];

    // Y축 설정
    const yScaleOptions = {
      beginAtZero: true,
      grid: {
        color: '#e1e2e4',
        tickColor: '#ABABAB',
        drawTicks: true,
      },
    };

    // Y축 설정을 추가
    chartOptions.scales = {
      ...chartOptions.scales,
      y: {
        ...(chartOptions.scales?.y ?? { type: 'linear' }), // 기본 타입을 linear로 설정
        ...yScaleOptions,
      },
    };

    const colorData = { ...chartData };
    colorData.datasets = colorData.datasets.map((dataset, index) => {
      if (chartColor.length > 0) {
        dataset.backgroundColor = chartColor[index % chartColor.length];
        dataset.borderColor = chartColor[index % chartColor.length];
      }
      return dataset;
    });

    // 차트 생성
    if (chartRef.current) {
      chart.current = new Chartjs(chartRef.current, {
        type: chartType,
        data: colorData,
        options: chartOptions,
      });
    } else {
      console.error('chartRef.current is null. Cannot create chart.');
    }

    // 컴포넌트 언마운트 시 차트 해제
    return () => {
      if (chart.current) {
        chart.current.destroy();
      }
    };
  }, [chartType, chartData, chartOptions]);

  return (
    <div className={clsx('dsx-Chart', `dsx-Chart--${chartType}`, className)}>
      <canvas ref={chartRef} role="img" aria-label={chartLabel} {...props}></canvas>
      {chartType === 'doughnut' && chartInfo && (
        <div className="dsx-Chart-info">
          <strong className="info-title">
            {chartInfo.title}
            {chartInfo.titleUnit && <span className="unit">{chartInfo.titleUnit}</span>}
          </strong>
          <span className="info-desc">
            {chartInfo.desc}
            {chartInfo.descUnit && <span className="unit">{chartInfo.descUnit}</span>}
          </span>
        </div>
      )}
    </div>
  );
};
